import * as Sentry from "@sentry/react";

type LogLevel = 'debug' | 'info' | 'warning' | 'error';

interface LogContext {
  [key: string]: any;
}

interface LogOptions {
  level?: LogLevel;
  context?: LogContext;
  tags?: Record<string, string>;
  user?: {
    id?: string;
    email?: string;
    username?: string;
  };
}

class Logger {
  private isDevMode: boolean;

  constructor() {
    this.isDevMode = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';
  }

  private get isSentryEnabled(): boolean {
    try {
      // Check if Sentry is properly initialized at runtime
      return Sentry.getClient() !== undefined;
    } catch {
      return false;
    }
  }

  /**
   * Log an error with Error object
   */
  logError(error: Error, options: Omit<LogOptions, 'level'> = {}) {
    const { context, tags, user } = options;

    // Only log to console in dev mode
    if (this.isDevMode) {
      console.error('🚨 Error:', error);
      if (context) console.error('Context:', context);
      if (tags) console.error('Tags:', tags);
      if (user) console.error('User:', user);
    }

    // Only send to Sentry if it's enabled (production with Sentry)
    if (this.isSentryEnabled) {
      Sentry.withScope((scope) => {
        // Set user context
        if (user) {
          scope.setUser(user);
        }

        // Set tags
        if (tags) {
          Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });
        }

        // Set context/extra data
        if (context) {
          Object.entries(context).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }

        scope.setLevel('error');
        Sentry.captureException(error);
      });
    }

    // If neither dev mode nor Sentry enabled, do nothing (silent in prod)
  }

  /**
   * Log a message
   */
  logMessage(message: string, options: LogOptions = {}) {
    const { level = 'info', context, tags, user } = options;

    // Only log to console in dev mode
    if (this.isDevMode) {
      const emoji = this.getLogEmoji(level);
      console.log(`${emoji} ${level.toUpperCase()}: ${message}`);
      if (context) console.log('Context:', context);
      if (tags) console.log('Tags:', tags);
      if (user) console.log('User:', user);
    }

    // Only send to Sentry if it's enabled (production with Sentry)
    if (this.isSentryEnabled) {
      Sentry.withScope((scope) => {
        // Set user context
        if (user) {
          scope.setUser(user);
        }

        // Set tags
        if (tags) {
          Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });
        }

        // Set context/extra data
        if (context) {
          Object.entries(context).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }

        scope.setLevel(level);
        Sentry.captureMessage(message, level);
      });
    }

    // If neither dev mode nor Sentry enabled, do nothing (silent in prod)
  }

  /**
   * Log debug information (only shows in dev mode)
   */
  debug(message: string, context?: LogContext) {
    if (this.isDevMode) {
      console.debug('🐛 DEBUG:', message);
      if (context) console.debug('Context:', context);
    }
    // Debug messages are not sent to Sentry in production
  }

  /**
   * Log info message
   */
  info(message: string, options: Omit<LogOptions, 'level'> = {}) {
    this.logMessage(message, { ...options, level: 'info' });
  }

  /**
   * Log warning message
   */
  warning(message: string, options: Omit<LogOptions, 'level'> = {}) {
    this.logMessage(message, { ...options, level: 'warning' });
  }

  /**
   * Log error message (without Error object)
   */
  error(message: string, options: Omit<LogOptions, 'level'> = {}) {
    this.logMessage(message, { ...options, level: 'error' });
  }

  /**
   * Add breadcrumb for debugging (works in both dev and prod)
   */
  addBreadcrumb(message: string, category: string = 'custom', data?: LogContext) {
    // Only log to console in dev mode
    if (this.isDevMode) {
      console.log(`🍞 BREADCRUMB [${category}]: ${message}`);
      if (data) console.log('Data:', data);
    }

    // Only send to Sentry if it's enabled
    if (this.isSentryEnabled) {
      Sentry.addBreadcrumb({
        message,
        category,
        level: 'info',
        data,
      });
    }

    // If neither dev mode nor Sentry enabled, do nothing (silent in prod)
  }

  /**
   * Set user context for subsequent logs
   */
  setUser(user: { id?: string; email?: string; username?: string }) {
    // Only log to console in dev mode
    if (this.isDevMode) {
      console.log('👤 Setting user context:', user);
    }

    // Only send to Sentry if it's enabled
    if (this.isSentryEnabled) {
      Sentry.setUser(user);
    }

    // If neither dev mode nor Sentry enabled, do nothing (silent in prod)
  }

  /**
   * Clear user context
   */
  clearUser() {
    // Only log to console in dev mode
    if (this.isDevMode) {
      console.log('👤 Clearing user context');
    }

    // Only send to Sentry if it's enabled
    if (this.isSentryEnabled) {
      Sentry.setUser(null);
    }

    // If neither dev mode nor Sentry enabled, do nothing (silent in prod)
  }

  private getLogEmoji(level: LogLevel): string {
    switch (level) {
      case 'debug': return '🐛';
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'error': return '🚨';
      default: return 'ℹ️';
    }
  }
}

// Export singleton instance
export const logging = new Logger();

// Export types for external use
export type { LogLevel, LogContext, LogOptions };
